import { useEffect, useState } from "react";

import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { usePostHog } from "posthog-js/react";

import { ErrorTypography } from "components/ErrorTypography";
import { LoadingWrappedNode } from "components/LoadingWrappedNode";
import {
  ClientAppEvent,
  EVOTRUX_BLUE,
  EVOTRUX_BLUE_DARK,
} from "constants/index";
import { useSwrFetch, useWriterComponentState } from "hooks";

interface PendingAgreement {
  agreement_type: string;
  version: number;
  body: string;
}

interface PendingAgreementsResponse {
  pending_agreements: PendingAgreement[];
}

interface AcceptAgreementResponse {
  agreement_type: string;
  version: number;
  accepted_at: string;
  already_accepted: boolean;
}

interface Props {
  shipmentId: number | null;
  children: React.ReactNode;
}

function buildAgreementAnalyticsPayload(
  agreement: PendingAgreement,
  shipmentId: number | null,
) {
  return { agreement_type: agreement.agreement_type, shipment_id: shipmentId };
}

// Blocks children behind a non-dismissible Dialog until GET /agreements/pending
// comes back empty; accepting revalidates the list to unmount the gate.
export function AgreementModal(props: Props) {
  const { shipmentId, children } = props;
  const posthog = usePostHog();
  const [agreed, setAgreed] = useState<boolean>(false);

  const {
    data,
    loading: pendingLoading,
    error: pendingError,
    refetch: refetchPending,
  } = useSwrFetch<PendingAgreementsResponse>({
    url:
      shipmentId != null
        ? `/agreements/pending?shipment_id=${shipmentId}`
        : "/agreements/pending",
    errorMessage:
      "Failed to confirm you're clear to quote this shipment. Please contact us if the problem persists.",
  });

  const pendingAgreement = data?.pending_agreements[0] ?? null;

  const {
    write: acceptAgreement,
    loading: acceptLoading,
    error: acceptError,
  } = useWriterComponentState({
    errorMessage:
      "Failed to record your agreement. Please contact us if the problem persists.",
    writeConfig: {
      method: "post",
      url: `/agreements/${pendingAgreement?.agreement_type}/accept`,
    },
  });

  useEffect(() => {
    if (pendingAgreement != null) {
      posthog?.capture(
        ClientAppEvent.CARRIER_VIEWED_ANTI_SOLICITATION_AGREEMENT,
        buildAgreementAnalyticsPayload(pendingAgreement, shipmentId),
      );
    }
  }, [pendingAgreement, posthog, shipmentId]);

  if (pendingLoading) {
    return <LoadingWrappedNode loading>{null}</LoadingWrappedNode>;
  }

  if (pendingError != null) {
    // Failing open here would let any carrier quote while the agreement
    // check itself is broken, defeating the whole point of the gate — block
    // and let them retry instead of silently falling through to children.
    return (
      <Dialog
        open
        disableEscapeKeyDown
        maxWidth="sm"
        fullWidth
        aria-labelledby="agreement-modal-title"
        PaperProps={{ sx: { borderRadius: "8px" } }}
      >
        <DialogContent sx={{ px: 4, pt: 4, pb: 2 }}>
          <Typography
            id="agreement-modal-title"
            component="h2"
            sx={{ fontWeight: 500, fontSize: "22px", mb: 1 }}
          >
            Carrier Agreement
          </Typography>
          <Box sx={{ borderBottom: `3px solid ${EVOTRUX_BLUE}`, mb: 2.5 }} />
          <ErrorTypography error={pendingError} />
        </DialogContent>
        <DialogActions sx={{ px: 4, pb: 4, pt: 0 }}>
          <Tooltip title="Check again for outstanding agreements.">
            <span style={{ width: "100%" }}>
              <Button
                variant="contained"
                fullWidth
                onClick={() => refetchPending()}
                sx={{
                  fontWeight: 600,
                  fontSize: "15px",
                  textTransform: "none",
                  backgroundColor: EVOTRUX_BLUE,
                  borderRadius: "6px",
                  py: 1.25,
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: EVOTRUX_BLUE_DARK,
                    boxShadow: "none",
                  },
                }}
              >
                Retry
              </Button>
            </span>
          </Tooltip>
        </DialogActions>
      </Dialog>
    );
  }

  if (pendingAgreement == null) {
    return <>{children}</>;
  }

  const onConfirm = () => {
    acceptAgreement<AcceptAgreementResponse>(
      (response) => {
        if (!response.already_accepted) {
          posthog?.capture(
            ClientAppEvent.CARRIER_ACCEPTED_ANTI_SOLICITATION_AGREEMENT,
            buildAgreementAnalyticsPayload(pendingAgreement, shipmentId),
          );
        }
        setAgreed(false);
        refetchPending();
      },
      {
        version: pendingAgreement.version,
        shipment_id: shipmentId,
        // get_session_id() returns "" (not null/undefined) when there's no
        // active session, so ?? wouldn't catch it — the backend's CharField
        // rejects a blank string, only null or a real value.
        posthog_session_id: posthog?.get_session_id() || null,
      },
    );
  };

  return (
    <Dialog
      open
      disableEscapeKeyDown
      maxWidth="sm"
      fullWidth
      aria-labelledby="agreement-modal-title"
      PaperProps={{ sx: { borderRadius: "8px" } }}
    >
      <DialogContent sx={{ px: 4, pt: 4, pb: 2 }}>
        <Typography
          id="agreement-modal-title"
          component="h2"
          sx={{ fontWeight: 500, fontSize: "22px", mb: 1 }}
        >
          Carrier Agreement
        </Typography>
        <Box sx={{ borderBottom: `3px solid ${EVOTRUX_BLUE}`, mb: 2.5 }} />
        <Stack direction="column" spacing={2}>
          <Typography sx={{ fontWeight: 500, fontSize: "15px" }}>
            To keep Evotrux fair for everyone, we ask all carriers to agree to
            the following.
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            whiteSpace="pre-line"
            sx={{ lineHeight: 1.6 }}
          >
            {pendingAgreement.body}
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={agreed}
                onChange={(event) => setAgreed(event.target.checked)}
                sx={{
                  color: EVOTRUX_BLUE,
                  "&.Mui-checked": { color: EVOTRUX_BLUE },
                }}
              />
            }
            label={
              <Typography sx={{ fontWeight: 500, fontSize: "14px" }}>
                I agree
              </Typography>
            }
          />
          <ErrorTypography error={acceptError} />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 4, pb: 4, pt: 0 }}>
        <Tooltip title="Records your agreement and lets you continue quoting this shipment.">
          <span style={{ width: "100%" }}>
            <Button
              variant="contained"
              fullWidth
              disabled={!agreed || acceptLoading}
              onClick={onConfirm}
              sx={{
                fontWeight: 600,
                fontSize: "15px",
                textTransform: "none",
                backgroundColor: EVOTRUX_BLUE,
                borderRadius: "6px",
                py: 1.25,
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: EVOTRUX_BLUE_DARK,
                  boxShadow: "none",
                },
              }}
            >
              Agree &amp; Continue
            </Button>
          </span>
        </Tooltip>
      </DialogActions>
    </Dialog>
  );
}
